import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { DynIcon } from "@/components/site/DynIcon";
import { Btn, Reveal, SectionHeading } from "@/components/site/kit";
import { useApi } from "@/lib/api";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, Number(value) || 0, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, value]);
  return <span ref={ref}>{n.toLocaleString("en-IN")}{suffix}</span>;
};

export const StatsBar = ({ stats = [] }) => (
  <div className="card-base grid grid-cols-2 divide-line lg:grid-cols-4 lg:divide-x" data-testid="stats-section">
    {stats.map((s, i) => (
      <div key={s.label} className="p-6 text-center sm:p-8" data-testid={`stat-${i}`}>
        <p className="font-heading text-4xl font-extrabold text-navy sm:text-5xl"><Counter value={s.value} suffix={s.suffix} /></p>
        <p className="mt-2 text-sm font-semibold text-muted2">{s.label}</p>
      </div>
    ))}
  </div>
);

export const ServicesGrid = ({ detailed = false }) => {
  const { data } = useApi("/services");
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
      {(data || []).map((s, i) => (
        <Reveal key={s.id} delay={(i % 3) * 0.08} className="h-full">
          <article className="card-base card-hover group h-full p-7" data-testid={`service-card-${s.slug}`}>
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-indigo-brand text-sky transition-colors group-hover:bg-ocean group-hover:text-white"><DynIcon name={s.icon} className="h-7 w-7" /></span>
            <h3 className="mt-6 text-xl font-bold">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-body">{s.short_description}</p>
            {detailed && (
              <ul className="mt-5 space-y-2">
                {(s.features || []).map((f) => <li key={f} className="flex gap-2 text-sm text-body"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{f}</li>)}
              </ul>
            )}
          </article>
        </Reveal>
      ))}
    </div>
  );
};

export const WhyUs = ({ why }) => (
  <section className="indigo-gradient section relative overflow-hidden" data-testid="why-us-section">
    <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-sky/10 blur-3xl" aria-hidden="true" />
    <div className="container-x relative">
      <SectionHeading light badge="Why choose us" title={why?.title} highlight={why?.highlight} subtitle={why?.subtitle} />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {(why?.items || []).map((it, i) => (
          <Reveal key={it.title} delay={(i % 3) * 0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.06] p-7 backdrop-blur-sm transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-white/10" data-testid={`why-us-item-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-cta text-navy"><DynIcon name={it.icon} className="h-6 w-6" /></span>
              <h3 className="mt-5 text-lg font-bold text-white">{it.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{it.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ProcessTimeline = ({ steps = [] }) => (
  <div className="relative" data-testid="process-timeline">
    <div className="absolute left-0 right-0 top-7 hidden h-0.5 bg-gradient-to-r from-sky/20 via-sky to-sky/20 lg:block" aria-hidden="true" />
    <ol className="grid gap-8 lg:grid-cols-5 lg:gap-6">
      {steps.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.1}>
          <li className="relative flex gap-5 lg:block" data-testid={`process-step-${i}`}>
            <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-2xl border-4 border-white bg-ocean text-white shadow-soft"><DynIcon name={s.icon} className="h-6 w-6" /></span>
            <div className="lg:mt-6">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky">Step {i + 1}</p>
              <h3 className="mt-1 text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-body">{s.description}</p>
            </div>
          </li>
        </Reveal>
      ))}
    </ol>
  </div>
);

export const TechStrip = ({ items = [] }) => (
  <div className="relative overflow-hidden py-2 [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]" data-testid="tech-strip">
    <div className="flex w-max animate-marquee gap-4">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="whitespace-nowrap rounded-full border border-line bg-white px-6 py-3 font-heading text-sm font-semibold text-navy shadow-soft">{t}</span>
      ))}
    </div>
  </div>
);

export const CtaBanner = ({ title, subtitle, button, to = "/contact#quote" }) => (
  <Reveal>
    <div className="indigo-gradient relative overflow-hidden rounded-3xl px-7 py-14 text-center sm:px-14" data-testid="cta-banner">
      <div className="dot-grid absolute inset-0 opacity-20" aria-hidden="true" />
      <div className="relative mx-auto max-w-2xl">
        <h2 className="text-3xl font-extrabold text-white sm:text-4xl">{title}</h2>
        {subtitle && <p className="mt-4 text-white/75 md:text-lg">{subtitle}</p>}
        <Btn to={to} className="mt-8" testId="cta-banner-btn">{button || "Get a Free Quote"}</Btn>
      </div>
    </div>
  </Reveal>
);
